import { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  FormControl,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  AUDIENCE_COUNT_ENDPOINT,
  AUDIENCE_SEGMENT_LIST_ENDPOINT,
} from "../../../config/urls";
import { useAuth } from "../../../hooks/AuthProvider";
import typography from "../../../cssUtils/typography";
import AppDropdown from "../../../common/app-dropdown";

const SelectSegment = ({
  prefetchedSegmentName,
  selectedSegment,
  setSelectedSegment,
  phoneNumberCount,
  setPhoneNumberCount,
  campaignsData,
  isEditMode,
  audienceError,
  setAudienceError
}) => {
  const { getValidToken } = useAuth();

  const [selectedSegmentId, setSelectedSegmentId] = useState("");
  const [segments, setSegments] = useState([]);

  // Sync segment ID when parent changes
  useEffect(() => {
    if (selectedSegment) {
      setSelectedSegmentId(selectedSegment.id);
    } else {
      setSelectedSegmentId("");
    }
  }, [selectedSegment]);

  // Fetch segment list
  const fetchData = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(
        AUDIENCE_SEGMENT_LIST_ENDPOINT + "?limit=1000",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      const jsonData = await response.json();
      setSegments(jsonData.results);
    } catch (error) {
      console.error("Error fetching segments:", error.message);
    }
  };

  // Fetch audience count
  const fetchSegmentAudienceCount = async () => {
    if (!selectedSegment) {
      setPhoneNumberCount(0);
      return;
    }

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    let queryParams = new URLSearchParams({
      limit: 1,
      offset: 0,
    });

    selectedSegment.filters.forEach(({ field, operator, value }) => {
      field = field.toLowerCase().replaceAll(" ", "_");
      operator = operator.toLowerCase().replaceAll(" ", "_");
      queryParams.append(field, `${operator}:${value ?? ""}`);
    });

    try {
      const response = await fetch(
        `${AUDIENCE_COUNT_ENDPOINT}?${queryParams}`,
        { headers }
      );

      if (!response.ok) throw new Error("Failed to fetch data");

      const jsonData = await response.json();
      setPhoneNumberCount(jsonData);
    } catch (error) {
      console.error("Error fetching audience count:", error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!prefetchedSegmentName || !segments.length) return;

    const matchedSegment = segments.find(
      (segment) => segment.name === prefetchedSegmentName
    );

    if (matchedSegment) {
      setSelectedSegment(matchedSegment);
      setSelectedSegmentId(matchedSegment.id);
    }
  }, [prefetchedSegmentName, segments]);

  useEffect(() => {
    fetchSegmentAudienceCount();
  }, [selectedSegment]);

  useEffect(() => {
    setAudienceError(selectedSegment && phoneNumberCount === 0);
  }, [selectedSegment, phoneNumberCount]);

  const handleSegmentSelect = (value, item) => {
    setSelectedSegment(item.raw);
    setSelectedSegmentId(value);
  };

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <AppDropdown
            menuItems={segments.map((segment) => ({
              label: segment.name,
              value: segment.id,
              raw: segment,
            }))}
            selectedValue={selectedSegmentId}
            onMenuSelect={handleSegmentSelect}
            variant="outlined"
            disabled={isEditMode}
            endIcon={<ExpandMoreIcon />}
            paperProps={{
              maxHeight: "200px",
              overflowY: "auto",
            }}
            sx={{ width: "100%" }}
          >
            <span style={{ ...typography.bodyLarge }}>
              {selectedSegment ? selectedSegment.name : "Select a Segment *"}
            </span>
          </AppDropdown>

          {audienceError && (
            <Typography 
              color="error" 
              sx={{ 
                ...typography.bodySmall, 
                mt: 1,
                color: "#d32f2f" 
              }}
            >
              The selected segment list does not have any audience
            </Typography>
          )}

          {phoneNumberCount > 0 && !audienceError && (
            <Typography 
              sx={{ 
                ...typography.bodySmall, 
                mt: 1,
                color: "#505050" 
              }}
            >
              Phone number count: {phoneNumberCount}
            </Typography>
          )}
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default SelectSegment;